/**
 * UI predicates for the admin Change Plan dialog's "Sync with Stripe"
 * checkbox.
 *
 * Extracted from `components/billing/tenants-tab.tsx` so the matrix of
 * tenant states (Stripe + live sub / Stripe + free-flip / Stripe + canceled /
 * manual / shopify / mock) can be unit-tested without rendering the React
 * component.
 *
 * Decisions:
 *   - Checkbox is hidden for non-Stripe rows entirely. Manual / shopify / mock
 *     never need the proration sync; the local-only PATCH is the only path.
 *   - Stripe rows always show the checkbox so super-user has the option to
 *     fall back to local-only.
 *   - Default-on only when a live Stripe subscription exists AND its status
 *     is syncable. Free-flip rows (`stripe_subscription_id=null`) and
 *     terminal-status rows (`canceled`, etc.) get checkbox visible-unchecked
 *     because syncing would 400 (`no_external_subscription` /
 *     `subscription_not_active`).
 */

/**
 * Subscription statuses that the admin proration handler will accept.
 * Mirrors `SYNCABLE_SUBSCRIPTION_STATUSES` in the handler — kept duplicated
 * so the UI predicate stays a pure function with no handler dependency.
 */
const SYNCABLE_STATUSES: ReadonlySet<string> = new Set(['active', 'past_due', 'trialing'])

interface ChangePlanCheckboxRow {
  payment_provider: string | null
  stripe_subscription_id: string | null
  subscription_status: string | null
}

export function showSyncWithStripeCheckbox(row: ChangePlanCheckboxRow): boolean {
  return row.payment_provider === 'stripe'
}

export function defaultSyncWithStripeChecked(row: ChangePlanCheckboxRow): boolean {
  if (!showSyncWithStripeCheckbox(row)) return false
  if (row.stripe_subscription_id === null) return false
  return row.subscription_status !== null && SYNCABLE_STATUSES.has(row.subscription_status)
}

/**
 * Adaptive description string for the Change Plan dialog body.
 *
 * Branches on the three "doomed if synced" reasons separately so the toast
 * the admin will see (`no_external_subscription` / `subscription_not_active`)
 * matches the dialog wording. `subscriptionStatus` is the raw column value
 * — null means we treat the row as not-syncable, same as a terminal status.
 */
export function changePlanDialogDescription(args: {
  syncChecked: boolean
  hasLiveSub: boolean
  subscriptionStatus: string | null
}): string {
  if (!args.syncChecked) {
    return '⚠ Local-only change — Stripe subscription keeps billing on the old plan. Use only for data reconciliation or known-manual cases. Creates split-brain risk on self-serve tenants.'
  }
  if (!args.hasLiveSub) {
    return '⚠ Tenant has no live Stripe subscription (free-flip state). Sync will fail — use Enable self-serve to create the initial Stripe checkout on the new plan.'
  }
  if (args.subscriptionStatus === null || !SYNCABLE_STATUSES.has(args.subscriptionStatus)) {
    return `⚠ Stripe subscription is in status '${args.subscriptionStatus ?? 'unknown'}' — Stripe sync will fail. Re-enable self-serve checkout to create a new subscription on the target plan.`
  }
  return 'Updates the Stripe subscription. Upgrade: the tenant\'s card is charged the full new price now and a new billing period starts; the new plan\'s credits come with that invoice. Downgrade: the billing cycle is preserved and the new (lower) price applies on the next invoice. Nothing is refunded, and credits already granted stay.'
}
